/**
 * Local-date helpers (Iter 65 P2).
 *
 * Use these instead of `Date.toISOString()` / `new Date("YYYY-MM-DD")` when working
 * with date-only values (calendars, availability ranges, booking start/end days).
 * `Date.toISOString()` round-trips through UTC and silently shifts the day for any
 * user east or west of UTC (selecting Sept 5 in CEST otherwise becomes "2026-09-04").
 *
 * Rule of thumb:
 *   - Calendars / pickers / day-level fields → use `toLocalIso` + `fromLocalIso`.
 *   - Timestamps with time-of-day (created_at, etc.) → stick with native `Date` + UTC.
 */

const _pad = (n) => String(n).padStart(2, "0");

/**
 * Format a JS Date as "YYYY-MM-DD" using its LOCAL year/month/day parts.
 * Returns "" when `d` is falsy or invalid.
 */
export function toLocalIso(d) {
  if (!d) return "";
  const t = d instanceof Date ? d : new Date(d);
  if (isNaN(t.getTime())) return "";
  return `${t.getFullYear()}-${_pad(t.getMonth() + 1)}-${_pad(t.getDate())}`;
}

/**
 * Parse "YYYY-MM-DD" (or any string starting with that pattern, e.g. an ISO
 * timestamp) into a LOCAL-midnight `Date`. Returns `undefined` when the input
 * is missing or unparseable.
 *
 * Why not `new Date(iso)`? Because that parses ISO date-only strings as UTC
 * midnight, which renders as the previous day for negative-offset users.
 */
export function fromLocalIso(iso) {
  if (!iso) return undefined;
  const datePart = String(iso).slice(0, 10);
  const [y, m, d] = datePart.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);  // local midnight — never UTC
}

/**
 * Render an ISO date string (or Date) as a human-friendly "DD MMM YYYY"
 * label in the user's locale. Uses `fromLocalIso` so the displayed day
 * matches what the user actually picked.
 */
export function formatLocalDay(iso, { fallback = "—" } = {}) {
  const d = fromLocalIso(iso);
  if (!d) return fallback;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * True if `date` (a JS `Date` at LOCAL midnight) falls within any range in
 * `ranges`. Each range is `{ start_date, end_date }` with ISO date strings.
 * Used by date pickers to disable already-booked days.
 */
export function dateInRanges(date, ranges) {
  if (!date || !Array.isArray(ranges) || ranges.length === 0) return false;
  const t = date.getTime();
  return ranges.some((r) => {
    const rs = fromLocalIso(r.start_date)?.getTime() ?? new Date(r.start_date).getTime();
    const re = fromLocalIso(r.end_date)?.getTime() ?? new Date(r.end_date).getTime();
    return t >= rs && t < re;
  });
}

/**
 * Local-midnight `Date` for today — handy as a `min` boundary on calendars
 * so users can't book days in the past.
 */
export function localToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}
